import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import {
  OrderReviewItemResponse,
  OrderReviewResponse,
} from "../../../models/order";
import { CognitoService } from "../../../services/cognito-service";
import { OrdersService } from "../../../services/orders-service";

export interface ReviewInvoiceState {
  loading: boolean;
  total: number;
  orderItems: OrderReviewItemResponse[];
  readableElectionDate: string;
  processingModalOpened: boolean;
  messages: string[];
  requestPrinting: boolean;
  printingCost: number;

  updateRequestPrinting: () => void;
  cancel: () => void;
  submitInvoice: () => void;
}

const useReviewInvoice = (): ReviewInvoiceState => {
  const navigate = useNavigate();
  const params = useParams<{ typeId: string; electionDate: string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [readableElectionDate, setReadableElectionDate] = useState<string>("");
  const [order, setOrder] = useState<OrderReviewResponse>();
  const [total, setTotal] = useState<number>(0);
  const [token, setToken] = useState<string>("");
  const [shouldConnect, setShouldConnect] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[]>([]);
  const [requestPrinting, setRequestPrinting] = useState<boolean>(false);
  const [processingModalOpened, setProcessingModalOpened] =
    useState<boolean>(false);
  const [baseTotal, setBaseTotal] = useState<number>(0);
  const [printingCost, setPrintingCost] = useState<number>(0);
  useEffect(() => {
    (async () => {
      const { typeId, electionDate } = params;
      const order = await OrdersService.getReview(+typeId!, electionDate!);
      setOrder(order);
      setTotal(order.items.reduce((prev, curr) => prev + curr.price, 0));
      setBaseTotal(order.items.reduce((prev, curr) => prev + curr.price, 0))
      setLoading(false);
    })();
  }, [params]);

  useEffect(() => {
    if (order) {
      const splitDate = order.electionDate.split("T")[0].split("-");
      setReadableElectionDate(
        new Date(`${splitDate[1]}-${splitDate[2]}-${splitDate[0]}`).toLocaleString("en", {
          day: "numeric",
          month: "long",
          year: "numeric",
        })
      );
    }
  }, [order]);

  useEffect(() => {
    (async () => {
      const session = await CognitoService.getSession();
      if (session) setToken(session.getIdToken().getJwtToken());
    })();
  }, []);

  const cancel = () => {
    navigate(-1);
  };

  const getWebsocketUrl = useCallback(() => {
    if (!order?.electionDate) return "";
    return `${process.env.REACT_APP_SERVICE_URL?.replace(
      "http://",
      "ws://"
    ).replace("https://", "wss://")}/orders/submit/${params.typeId}/${order.electionDate}/${requestPrinting}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.typeId, order, requestPrinting]);

  useWebSocket(
    getWebsocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => false,
      onClose: (event: WebSocketEventMap["close"]) => {
        navigate(
          "/order" + "/" + messages.at(-1)?.split("Order: ")[1]
        );
      },
      onMessage: (event: WebSocketEventMap["message"]) => {
        setMessages((messages) => [...messages, event.data]);
      },
      onError: (event: WebSocketEventMap["error"]) => {
        console.log("error", event);
      },
      protocols: ["Authorization", token],
    },
    shouldConnect
  );

  const submitInvoice = () => {
    if (!order?.electionDate) return;

    setProcessingModalOpened(true);
    setShouldConnect(true);
  };

  const updateRequestPrinting = () => {
    setRequestPrinting(!requestPrinting);
    setPrintingCost(order!.items.length * 10)
    setTotal(!requestPrinting ? baseTotal + order!.items!.length * 10 : baseTotal)
  }

  return {
    loading,
    total,
    orderItems: order?.items || [],
    readableElectionDate,
    processingModalOpened,
    messages,
    requestPrinting,
    printingCost,

    updateRequestPrinting,
    cancel,
    submitInvoice,
    // openProcessingModal: () => setProcessingModalOpened(true),
    // closeProcessingModal: () => setProcessingModalOpened(false),
  };
};

export default useReviewInvoice;
